import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Globe, ArrowUpRight, BookOpenText } from 'lucide-react';
import { FaTelegramPlane } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { useTranslation } from 'react-i18next';
import logo from '../../assets/logo_logo.svg';
import { landingLinks } from './landingContent';

const displayFont = 'Bahnschrift, "Trebuchet MS", "Segoe UI Variable Display", sans-serif';

const isExternalHref = (href) => /^https?:\/\//i.test(String(href || ''));

export function LandingActionLink({ href, children, className = '', ...rest }) {
  if (isExternalHref(href)) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className} {...rest}>
        {children}
      </a>
    );
  }

  return (
    <Link to={href} className={className} {...rest}>
      {children}
    </Link>
  );
}

function LanguageToggle() {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');

  return (
    <button
      type="button"
      onClick={() => i18n.changeLanguage(isEnglish ? 'ru' : 'en')}
      className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-300 transition-colors hover:border-cyan-400/35 hover:text-white"
    >
      <Globe className="h-3.5 w-3.5 text-cyan-300" />
      <span>{isEnglish ? 'EN' : 'RU'}</span>
    </button>
  );
}

export function SocialLinks({ className = '' }) {
  const itemClass =
    'landing-card inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-200 transition-colors hover:border-cyan-400/35 hover:text-white';

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <a href={landingLinks.telegram} target="_blank" rel="noopener noreferrer" className={itemClass} aria-label="Telegram">
        <FaTelegramPlane className="h-4 w-4" />
      </a>
      <a href={landingLinks.twitter} target="_blank" rel="noopener noreferrer" className={itemClass} aria-label="X / Twitter">
        <FaXTwitter className="h-4 w-4" />
      </a>
      <a href={landingLinks.guide} target="_blank" rel="noopener noreferrer" className={itemClass} aria-label="Guide">
        <BookOpenText className="h-4 w-4" />
      </a>
    </div>
  );
}

export function LandingBackdrop() {
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(37,99,235,0.18),_transparent_34%),radial-gradient(circle_at_bottom,_rgba(6,182,212,0.1),_transparent_32%),linear-gradient(180deg,_#020617_0%,_#04101f_52%,_#020617_100%)]" />
      <div className="landing-grid absolute inset-0 opacity-35" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-300/30 to-transparent" />
      <div className="landing-glow-primary absolute -left-24 top-24 h-[24rem] w-[24rem] rounded-full bg-blue-500/14 blur-3xl" />
      <div className="landing-glow-secondary absolute right-[-8rem] top-20 h-[28rem] w-[28rem] rounded-full bg-cyan-400/10 blur-3xl" />
      <div className="landing-glow-tertiary absolute bottom-[-10rem] left-1/2 h-[18rem] w-[32rem] -translate-x-1/2 rounded-full bg-sky-400/8 blur-3xl" />
    </div>
  );
}

export function LandingNavbar({ copy, appHref, appLabel }) {
  const [isOpen, setIsOpen] = useState(false);

  const navItems = useMemo(
    () => [
      { href: '#product', label: copy.nav.product },
      { href: '#flow', label: copy.nav.flow },
      { href: '#security', label: copy.nav.security },
      { href: '#guide', label: copy.nav.guide }
    ],
    [copy]
  );

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-slate-950/75 backdrop-blur-xl">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
        <a href="#top" className="flex items-center gap-3">
          <img src={logo} alt="Hedger" className="h-9 w-9" />
          <div className="text-[1.45rem] font-black leading-none text-white sm:text-[1.6rem]" style={{ fontFamily: displayFont }}>
            Hedger
          </div>
        </a>

        <nav className="hidden items-center gap-8 lg:flex">
          {navItems.map((item) => (
            <a key={item.href} href={item.href} className="text-sm font-medium text-slate-300 transition-colors hover:text-white">
              {item.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <LanguageToggle />
          <LandingActionLink href={appHref} className="inline-flex items-center gap-2 rounded-full border border-cyan-300/25 bg-cyan-300/10 px-5 py-2.5 text-sm font-semibold text-cyan-50 transition-colors hover:border-cyan-300/45 hover:bg-cyan-300/15">
            {appLabel}
            <ArrowUpRight className="h-4 w-4" />
          </LandingActionLink>
        </div>

        <div className="flex items-center gap-3 lg:hidden">
          <LanguageToggle />
          <button
            type="button"
            onClick={() => setIsOpen((state) => !state)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-200"
            aria-label="Toggle navigation"
          >
            {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="border-t border-white/10 bg-slate-950/95 px-4 py-4 lg:hidden">
          <div className="flex flex-col gap-3">
            {navItems.map((item) => (
              <a key={item.href} href={item.href} onClick={() => setIsOpen(false)} className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium text-slate-200">
                {item.label}
              </a>
            ))}
            <LandingActionLink href={appHref} className="inline-flex items-center justify-center gap-2 rounded-2xl border border-cyan-300/25 bg-cyan-300/10 px-4 py-3 text-sm font-semibold text-cyan-50">
              {appLabel}
              <ArrowUpRight className="h-4 w-4" />
            </LandingActionLink>
          </div>
        </div>
      )}
    </header>
  );
}

export function LandingFooter({ copy }) {
  return (
    <footer className="relative z-10 border-t border-white/10 px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-6xl flex-col gap-5 md:flex-row md:items-center md:justify-between">
        <p className="text-sm text-slate-400">{copy.footer.copy}</p>
        <SocialLinks />
      </div>
    </footer>
  );
}
